import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { eventFiles } from '@lexical/rich-text'
import { calculateZoomLevel, mergeRegister } from '@lexical/utils'
import type { LexicalEditor } from 'lexical'
import {
    $getNearestNodeFromDOMNode,
    $getNodeByKey,
    $getRoot,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    DRAGOVER_COMMAND,
    DROP_COMMAND
} from 'lexical'
import * as React from 'react'
import {type DragEvent as ReactDragEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { GripVertical } from 'lucide-react'

const SPACE = 4
const TARGET_LINE_HALF_HEIGHT = 2
const DRAGGABLE_BLOCK_MENU_CLASSNAME = 'draggable-block-menu'
const DRAG_DATA_FORMAT = 'application/x-lexical-drag-block'
const TEXT_BOX_HORIZONTAL_PADDING = 28

const Downward = 1
const Upward = -1
const Indeterminate = 0

let prevIndex = Infinity

export function isHTMLElement(x: any): x is HTMLElement {
    return x instanceof HTMLElement
}

export class Point {
    _x: number
    _y: number

    constructor(x: number, y: number) {
        this._x = x
        this._y = y
    }

    get x(): number {
        return this._x
    }

    get y(): number {
        return this._y
    }

    equals({ x, y }: { x: number; y: number }): boolean {
        return this.x === x && this.y === y
    }

    calcDeltaXTo({ x }: { x: number }): number {
        return this.x - x
    }

    calcDeltaYTo({ y }: { y: number }): number {
        return this.y - y
    }

    calcHorizontalDistanceTo(point: { x: number }): number {
        return Math.abs(this.calcDeltaXTo(point))
    }

    calcVerticalDistance(point: { y: number }): number {
        return Math.abs(this.calcDeltaYTo(point))
    }

    calcDistanceTo(point: { x: number; y: number }): number {
        return Math.sqrt(Math.pow(this.calcDeltaXTo(point), 2) + Math.pow(this.calcDeltaYTo(point), 2))
    }
}

export function isPoint(x: any): x is Point {
    return x instanceof Point
}

export class Rect {
    _top: number
    _right: number
    _left: number
    _bottom: number

    constructor(left: number, top: number, right: number, bottom: number) {
        const [physicTop, physicBottom] = top <= bottom ? [top, bottom] : [bottom, top]

        const [physicLeft, physicRight] = left <= right ? [left, right] : [right, left]

        this._top = physicTop
        this._right = physicRight
        this._left = physicLeft
        this._bottom = physicBottom
    }

    get top(): number {
        return this._top
    }

    get right(): number {
        return this._right
    }

    get bottom(): number {
        return this._bottom
    }

    get left(): number {
        return this._left
    }

    get width(): number {
        return Math.abs(this._left - this._right)
    }

    get height(): number {
        return Math.abs(this._bottom - this._top)
    }

    equals({ top, left, bottom, right }: { top: number; left: number; bottom: number; right: number }): boolean {
        return (
            top === this._top && bottom === this._bottom && left === this._left && right === this._right
        )
    }

    contains(target: Point | { top: number; left: number; bottom: number; right: number }): any {
        if (isPoint(target)) {
            const { x, y } = target

            const isOnTopSide = y < this._top
            const isOnBottomSide = y > this._bottom
            const isOnLeftSide = x < this._left
            const isOnRightSide = x > this._right

            const result = !isOnTopSide && !isOnBottomSide && !isOnLeftSide && !isOnRightSide

            return {
                reason: {
                    isOnBottomSide,
                    isOnLeftSide,
                    isOnRightSide,
                    isOnTopSide
                },
                result
            }
        } else {
            const { top, left, bottom, right } = target

            return (
                top >= this._top &&
                top <= this._bottom &&
                bottom >= this._top &&
                bottom <= this._bottom &&
                left >= this._left &&
                left <= this._right &&
                right >= this._left &&
                right <= this._right
            )
        }
    }

    intersectsWith(rect: Rect): boolean {
        const { left: x1, top: y1, width: w1, height: h1 } = rect
        const { left: x2, top: y2, width: w2, height: h2 } = this
        const maxX = x1 + w1 >= x2 + w2 ? x1 + w1 : x2 + w2
        const maxY = y1 + h1 >= y2 + h2 ? y1 + h1 : y2 + h2
        const minX = x1 <= x2 ? x1 : x2
        const minY = y1 <= y2 ? y1 : y2
        return maxX - minX <= w1 + w2 && maxY - minY <= h1 + h2
    }

    generateNewRect({ left = this.left, top = this.top, right = this.right, bottom = this.bottom }: { left?: number; top?: number; right?: number; bottom?: number }): Rect {
        return new Rect(left, top, right, bottom)
    }

    static fromLTRB(left: number, top: number, right: number, bottom: number): Rect {
        return new Rect(left, top, right, bottom)
    }

    static fromLWTH(left: number, width: number, top: number, height: number): Rect {
        return new Rect(left, top, left + width, top + height)
    }

    static fromPoints(startPoint: Point, endPoint: Point): Rect {
        const { y: top, x: left } = startPoint
        const { y: bottom, x: right } = endPoint
        return Rect.fromLTRB(left, top, right, bottom)
    }

    static fromDOM(dom: HTMLElement): Rect {
        const { top, width, left, height } = dom.getBoundingClientRect()
        return Rect.fromLWTH(left, width, top, height)
    }
}

function getCurrentIndex(keysLength: number): number {
    if (keysLength === 0) {
        return Infinity
    }
    if (prevIndex >= 0 && prevIndex < keysLength) {
        return prevIndex
    }

    return Math.floor(keysLength / 2)
}

function getTopLevelNodeKeys(editor: LexicalEditor): string[] {
    return editor.getEditorState().read(() => $getRoot().getChildrenKeys())
}

function getCollapsedMargins(elem: HTMLElement): { marginBottom: number; marginTop: number } {
    const getMargin = (element: Element | null, margin: keyof CSSStyleDeclaration): number =>
        element ? parseFloat(window.getComputedStyle(element)[margin] as string) : 0

    const { marginTop, marginBottom } = window.getComputedStyle(elem)
    const prevElemSiblingMarginBottom = getMargin(elem.previousElementSibling, 'marginBottom')
    const nextElemSiblingMarginTop = getMargin(elem.nextElementSibling, 'marginTop')
    const collapsedTopMargin = Math.max(parseFloat(marginTop), prevElemSiblingMarginBottom)
    const collapsedBottomMargin = Math.max(parseFloat(marginBottom), nextElemSiblingMarginTop)

    return { marginBottom: collapsedBottomMargin, marginTop: collapsedTopMargin }
}

function getBlockElement(anchorElem: HTMLElement, editor: LexicalEditor, event: MouseEvent, useEdgeAsDefault = false): HTMLElement | null {
    const anchorElementRect = anchorElem.getBoundingClientRect()
    const topLevelNodeKeys = getTopLevelNodeKeys(editor)

    let blockElem: HTMLElement | null = null

    editor.getEditorState().read(() => {
        if (useEdgeAsDefault) {
            const [firstNode, lastNode] = [
                editor.getElementByKey(topLevelNodeKeys[0]),
                editor.getElementByKey(topLevelNodeKeys[topLevelNodeKeys.length - 1])
            ]

            const [firstNodeRect, lastNodeRect] = [
                firstNode?.getBoundingClientRect(),
                lastNode?.getBoundingClientRect()
            ]

            if (firstNodeRect && lastNodeRect) {
                const firstNodeZoom = calculateZoomLevel(firstNode as HTMLElement)
                const lastNodeZoom = calculateZoomLevel(lastNode as HTMLElement)
                if (event.y / firstNodeZoom < firstNodeRect.top) {
                    blockElem = firstNode as HTMLElement
                } else if (event.y / lastNodeZoom > lastNodeRect.bottom) {
                    blockElem = lastNode as HTMLElement
                }

                if (blockElem) {
                    return
                }
            }
        }

        let index = getCurrentIndex(topLevelNodeKeys.length)
        let direction = Indeterminate

        while (index >= 0 && index < topLevelNodeKeys.length) {
            const key = topLevelNodeKeys[index]
            const elem = editor.getElementByKey(key)
            if (elem === null) {
                break
            }
            const zoom = calculateZoomLevel(elem)
            const point = new Point(event.x / zoom, event.y / zoom)
            const domRect = Rect.fromDOM(elem)
            const { marginTop, marginBottom } = getCollapsedMargins(elem)
            const rect = domRect.generateNewRect({
                bottom: domRect.bottom + marginBottom,
                left: anchorElementRect.left,
                right: anchorElementRect.right,
                top: domRect.top - marginTop
            })

            const {
                result,
                reason: { isOnTopSide, isOnBottomSide }
            } = rect.contains(point)

            if (result) {
                blockElem = elem
                prevIndex = index
                break
            }

            if (direction === Indeterminate) {
                if (isOnTopSide) {
                    direction = Upward
                } else if (isOnBottomSide) {
                    direction = Downward
                } else {
                    // stop search block element
                    direction = Infinity
                }
            }

            index += direction
        }
    })

    return blockElem
}

function isOnMenu(element: HTMLElement): boolean {
    return !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`)
}

function setMenuPosition(targetElem: HTMLElement | null, floatingElem: HTMLElement, anchorElem: HTMLElement): void {
    if (!targetElem) {
        floatingElem.style.opacity = '0'
        floatingElem.style.transform = 'translate(-10000px, -10000px)'
        return
    }

    const targetRect = targetElem.getBoundingClientRect()
    const targetStyle = window.getComputedStyle(targetElem)
    const floatingElemRect = floatingElem.getBoundingClientRect()
    const anchorElementRect = anchorElem.getBoundingClientRect()

    const top =
        targetRect.top +
        (parseInt(targetStyle.lineHeight || '0', 10) - floatingElemRect.height) / 2 -
        anchorElementRect.top

    const left = SPACE

    floatingElem.style.opacity = '1'
    floatingElem.style.transform = `translate(${left}px, ${top}px)`
}

function setDragImage(dataTransfer: DataTransfer, draggableBlockElem: HTMLElement): void {
    const { transform } = draggableBlockElem.style

    // Remove dragImage borders
    draggableBlockElem.style.transform = 'translateZ(0)'
    dataTransfer.setDragImage(draggableBlockElem, 0, 0)

    setTimeout(() => {
        draggableBlockElem.style.transform = transform
    })
}

function setTargetLine(targetLineElem: HTMLElement, targetBlockElem: HTMLElement, mouseY: number, anchorElem: HTMLElement): void {
    const { top: targetBlockElemTop, height: targetBlockElemHeight } =
        targetBlockElem.getBoundingClientRect()
    const { top: anchorTop, width: anchorWidth } = anchorElem.getBoundingClientRect()
    const { marginTop, marginBottom } = getCollapsedMargins(targetBlockElem)
    let lineTop = targetBlockElemTop
    if (mouseY >= targetBlockElemTop) {
        lineTop += targetBlockElemHeight + marginBottom / 2
    } else {
        lineTop -= marginTop / 2
    }

    const top = lineTop - anchorTop - TARGET_LINE_HALF_HEIGHT
    const left = TEXT_BOX_HORIZONTAL_PADDING - SPACE

    targetLineElem.style.transform = `translate(${left}px, ${top}px)`
    targetLineElem.style.width = `${anchorWidth - (TEXT_BOX_HORIZONTAL_PADDING - SPACE) * 2}px`
    targetLineElem.style.opacity = '.4'
}

function hideTargetLine(targetLineElem: HTMLElement | null): void {
    if (targetLineElem) {
        targetLineElem.style.opacity = '0'
        targetLineElem.style.transform = 'translate(-10000px, -10000px)'
    }
}

function useDraggableBlockMenu(editor: LexicalEditor, anchorElem: HTMLElement, isEditable: boolean): React.JSX.Element {
    const scrollerElem = anchorElem.parentElement

    const menuRef = useRef<HTMLDivElement>(null)
    const targetLineRef = useRef<HTMLDivElement>(null)
    const isDraggingBlockRef = useRef(false)
    const [draggableBlockElem, setDraggableBlockElem] = useState<HTMLElement | null>(null)

    useEffect(() => {
        function onMouseMove(event: MouseEvent) {
            const target = event.target
            if (!isHTMLElement(target)) {
                setDraggableBlockElem(null)
                return
            }

            if (isOnMenu(target)) {
                return
            }

            const _draggableBlockElem = getBlockElement(anchorElem, editor, event)

            setDraggableBlockElem(_draggableBlockElem)
        }

        function onMouseLeave() {
            setDraggableBlockElem(null)
        }

        scrollerElem?.addEventListener('mousemove', onMouseMove)
        scrollerElem?.addEventListener('mouseleave', onMouseLeave)

        return () => {
            scrollerElem?.removeEventListener('mousemove', onMouseMove)
            scrollerElem?.removeEventListener('mouseleave', onMouseLeave)
        }
    }, [scrollerElem, anchorElem, editor])

    useEffect(() => {
        if (menuRef.current) {
            setMenuPosition(draggableBlockElem, menuRef.current, anchorElem)
        }
    }, [anchorElem, draggableBlockElem])

    useEffect(() => {
        function onDragover(event: DragEvent): boolean {
            if (!isDraggingBlockRef.current) {
                return false
            }
            const [isFileTransfer] = eventFiles(event)
            if (isFileTransfer) {
                return false
            }
            const { pageY, target } = event
            if (!isHTMLElement(target)) {
                return false
            }
            const targetBlockElem = getBlockElement(anchorElem, editor, event as unknown as MouseEvent, true)
            const targetLineElem = targetLineRef.current
            if (targetBlockElem === null || targetLineElem === null) {
                return false
            }
            setTargetLine(targetLineElem, targetBlockElem, pageY / calculateZoomLevel(target), anchorElem)
            // Prevent default event to be able to trigger onDrop events
            event.preventDefault()
            return true
        }

        function onDrop(event: DragEvent): boolean {
            if (!isDraggingBlockRef.current) {
                return false
            }
            const [isFileTransfer] = eventFiles(event)
            if (isFileTransfer) {
                return false
            }
            const { target, dataTransfer, pageY } = event
            const dragData = dataTransfer?.getData(DRAG_DATA_FORMAT) || ''
            const draggedNode = $getNodeByKey(dragData)
            if (!draggedNode) {
                return false
            }
            if (!isHTMLElement(target)) {
                return false
            }
            const targetBlockElem = getBlockElement(anchorElem, editor, event as unknown as MouseEvent, true)
            if (!targetBlockElem) {
                return false
            }
            const targetNode = $getNearestNodeFromDOMNode(targetBlockElem)
            if (!targetNode) {
                return false
            }
            if (targetNode === draggedNode) {
                return true
            }
            const targetBlockElemTop = targetBlockElem.getBoundingClientRect().top
            if (pageY / calculateZoomLevel(target) >= targetBlockElemTop) {
                targetNode.insertAfter(draggedNode)
            } else {
                targetNode.insertBefore(draggedNode)
            }
            setDraggableBlockElem(null)

            return true
        }

        return mergeRegister(
            editor.registerCommand(
                DRAGOVER_COMMAND,
                (event) => {
                    return onDragover(event)
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                DROP_COMMAND,
                (event) => {
                    return onDrop(event)
                },
                COMMAND_PRIORITY_HIGH
            )
        )
    }, [anchorElem, editor])

    function onDragStart(event: ReactDragEvent<HTMLDivElement>) {
        const dataTransfer = event.dataTransfer
        if (!dataTransfer || !draggableBlockElem) {
            return
        }
        setDragImage(dataTransfer, draggableBlockElem)
        let nodeKey = ''
        editor.update(() => {
            const node = $getNearestNodeFromDOMNode(draggableBlockElem)
            if (node) {
                nodeKey = node.getKey()
            }
        })
        isDraggingBlockRef.current = true
        dataTransfer.setData(DRAG_DATA_FORMAT, nodeKey)
    }

    function onDragEnd() {
        isDraggingBlockRef.current = false
        hideTargetLine(targetLineRef.current)
    }

    return createPortal(
        <>
            <div
                className='draggable-block-menu flex items-center justify-center w-6 h-6 hover:bg-gray-100 rounded cursor-grab active:cursor-grabbing transition-colors'
                ref={menuRef}
                draggable={true}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
            >
                {isEditable && <GripVertical className="h-4 w-4 text-gray-400" />}
            </div>
            <div className='draggable-block-target-line absolute border-t-2 border-blue-400 opacity-0 pointer-events-none transition-opacity duration-150' ref={targetLineRef} />
        </>,
        anchorElem
    )
}

export default function DraggableBlockPlugin({ anchorElem = document.body }: { anchorElem?: HTMLElement }): React.JSX.Element {
    const [editor] = useLexicalComposerContext()
    return useDraggableBlockMenu(editor, anchorElem, editor.isEditable())
}
